import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { Dish, DishCategory, SemiFinishedProduct } from '../../types';

interface DishState {
  items: Dish[];
  categories: DishCategory[];
  loading: boolean;
}

const initialState: DishState = { items: [], categories: [], loading: false };
type BulkDeleteResult = { deletedIds: number[]; errors: string[] };

export const fetchDishes = createAsyncThunk('dish/fetchAll', async () => {
  const res = await api.get<Dish[]>('/dishes');
  return res.data;
});

export const createDish = createAsyncThunk('dish/create', async (data: any) => {
  const res = await api.post<Dish>('/dishes', data);
  return res.data;
});

export const updateDish = createAsyncThunk(
  'dish/update',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<Dish>(`/dishes/${id}`, data);
    return res.data;
  }
);

export const deleteDish = createAsyncThunk('dish/delete', async (id: number) => {
  await api.delete(`/dishes/${id}`);
  return id;
});

export const bulkDeleteDishes = createAsyncThunk('dish/bulkDelete', async (ids: number[]) => {
  const res = await api.post<BulkDeleteResult>('/dishes/bulk-delete', { ids });
  return res.data;
});

export const recalculateDishAndSemiCosts = createAsyncThunk(
  'dish/recalculateCosts',
  async () => {
    try {
      const res = await api.post<{ semiFinishedUpdated: number; dishesUpdated: number }>('/dishes/recalculate-costs');
      return res.data;
    } catch (err: any) {
      if (err?.response?.status !== 405) throw err;

      const [semiRes, dishRes] = await Promise.all([
        api.get<SemiFinishedProduct[]>('/semifinishedproducts'),
        api.get<Dish[]>('/dishes'),
      ]);

      const semis = semiRes.data;
      const dishes = dishRes.data;
      const semiById = new Map(semis.map((semi) => [semi.id, semi]));
      const visited = new Set<number>();
      const inStack = new Set<number>();
      const orderedSemiIds: number[] = [];

      const visitSemi = (semiId: number) => {
        if (visited.has(semiId)) return;
        if (inStack.has(semiId)) return;

        inStack.add(semiId);
        const semi = semiById.get(semiId);
        if (semi) {
          semi.items.forEach((item) => {
            if (item.subSemiFinishedProductId) visitSemi(item.subSemiFinishedProductId);
          });
        }
        inStack.delete(semiId);
        visited.add(semiId);
        orderedSemiIds.push(semiId);
      };

      semis.forEach((semi) => visitSemi(semi.id));

      for (const semiId of orderedSemiIds) {
        const semi = semiById.get(semiId);
        if (!semi) continue;

        const payload = {
          name: semi.name,
          b52Code: semi.b52Code ?? '',
          b52Name: semi.b52Name ?? '',
          categoryId: semi.categoryId,
          description: semi.description,
          cookingTime: semi.cookingTime,
          serveTime: semi.serveTime ?? 0,
          weightType: semi.weightType,
          netWeight: semi.netWeight,
          grossWeight: semi.grossWeight,
          excludeFromDishWeightCalc: semi.excludeFromDishWeightCalc,
          items: semi.items.map((item) => ({
            productId: item.productId,
            subSemiFinishedProductId: item.subSemiFinishedProductId,
            quantity: item.quantity,
            bruttoWeight: item.bruttoWeight,
            nettoWeight: item.nettoWeight,
            output: item.output,
            notes: item.notes ?? '',
          })),
        };

        const updatedSemi = await api.put<SemiFinishedProduct>(`/semifinishedproducts/${semi.id}`, payload);
        semiById.set(semi.id, updatedSemi.data);
      }

      for (const dish of dishes) {
        const payload = {
          name: dish.name,
          b52Code: dish.b52Code ?? '',
          b52Name: dish.b52Name ?? '',
          categoryId: dish.categoryId,
          description: dish.description,
          cookingTime: dish.cookingTime,
          serveTime: dish.serveTime ?? 0,
          technologyDescription: dish.technologyDescription,
          inventoryRequired: dish.inventoryRequired,
          photoUrl: dish.photoUrl,
          netWeight: dish.netWeight,
          grossWeight: dish.grossWeight,
          cleanOutput: dish.cleanOutput,
          servingsCount: dish.servingsCount ?? null,
          marginType: dish.marginType,
          margin: dish.margin,
          ingredients: dish.ingredients.map((ingredient) => ({
            productId: ingredient.productId,
            semiFinishedProductId: ingredient.semiFinishedProductId,
            quantity: ingredient.quantity,
            bruttoWeight: ingredient.bruttoWeight,
            nettoWeight: ingredient.nettoWeight,
            output: ingredient.output,
            notes: ingredient.notes ?? '',
          })),
        };

        await api.put<Dish>(`/dishes/${dish.id}`, payload);
      }

      return { semiFinishedUpdated: semis.length, dishesUpdated: dishes.length };
    }
  }
);

export const undoLastDishAndSemiCostRecalculation = createAsyncThunk(
  'dish/undoRecalculateCosts',
  async () => {
    const res = await api.post<{ restoredSemiFinished: number; restoredDishes: number }>('/dishes/recalculate-costs/undo-last');
    return res.data;
  }
);

export const uploadDishPhoto = createAsyncThunk(
  'dish/uploadPhoto',
  async ({ id, file }: { id: number; file: File }) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<{ url: string }>(`/dishes/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { id, url: res.data.url };
  }
);

export const fetchDishCategories = createAsyncThunk('dish/fetchCategories', async () => {
  const res = await api.get<DishCategory[]>('/dishcategories');
  return res.data;
});

export const createDishCategory = createAsyncThunk('dish/createCategory', async (data: any) => {
  const res = await api.post<DishCategory>('/dishcategories', data);
  return res.data;
});

export const updateDishCategory = createAsyncThunk(
  'dish/updateCategory',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<DishCategory>(`/dishcategories/${id}`, data);
    return res.data;
  }
);

export const deleteDishCategory = createAsyncThunk('dish/deleteCategory', async (id: number) => {
  await api.delete(`/dishcategories/${id}`);
  return id;
});

const dishSlice = createSlice({
  name: 'dish',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDishes.pending, (state) => { state.loading = true; })
      .addCase(fetchDishes.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchDishes.rejected, (state) => { state.loading = false; })
      .addCase(createDish.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateDish.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(deleteDish.fulfilled, (state, action) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(bulkDeleteDishes.fulfilled, (state, action) => {
        const deleted = new Set(action.payload.deletedIds);
        state.items = state.items.filter((i) => !deleted.has(i.id));
      })
      .addCase(fetchDishCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createDishCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      })
      .addCase(updateDishCategory.fulfilled, (state, action) => {
        const idx = state.categories.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.categories[idx] = action.payload;
      })
      .addCase(deleteDishCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((i) => i.id !== action.payload);
      });
  },
});

export default dishSlice.reducer;
